// archetypeExplainer.ts (CX9) - the tap-to-learn content source for the player TYPES, the read-training
// payoff made self-serve. The app already scripts these archetypes and grades your reads against them; this
// turns every archetype NAME it shows you into a term you can tap to learn (a) what that type IS and (b) what
// it means to YOU at the table.
//
// SINGLE SOURCE OF TRUTH. The type's identity - `label`, the one-line `description`, and the three concrete
// `signals` (tells) - is read straight from the shipped archetype corpus via `archetypeOf` (liveData.ts, the
// same D1/D2 JSON the engine reasons against). Nothing is duplicated. The ONE thing the corpus does not carry
// is the adjustment advice - "what it means for you" - because the JSON describes the villain, not hero's
// counter-strategy. That copy is authored here (ARCHETYPE_ADJUST), exploit-first and in the North Star voice,
// grounded in each type's real Five-Imbalances profile (never paraphrased): the Sticker overCalls so you
// value-bet and stop bluffing; the Rock overFolds so you steal and believe the rare raise; and so on.
//
// The projection lands on the SAME PopoverContent shape the glossary + coach debrief use, so the explainer
// opens the exact locked bottom-sheet (Glossary.tsx openContent) - no new bubble mechanism (CX9 spec: reuse,
// do not rebuild). `bullets` carries the tells so they render as a scannable list rather than a run-on.
import { ARCHETYPE_IDS, type ArchetypeId } from '@postflop/schema';
import type { PopoverContent } from '../components/Glossary';
import { archetypeOf } from '../engine/liveData';
import { displayName } from '../engine/liveRunner';

/**
 * "What it means for YOU at the table" - hero's counter-strategy for each type, the one piece of the
 * explainer the archetype JSON does not carry. Exploit-first (North Star §4.3): each line is the sound
 * low-stakes adjustment against that type's documented leak, keyed to its real imbalances. Second person,
 * concrete, why-driven - the same register as the description + the glossary. No em-dashes (project rule).
 */
export const ARCHETYPE_ADJUST: Record<ArchetypeId, string> = {
  rock:
    'Attack his blinds and c-bets freely, because he folds almost everything and hands you the pot. But when the Rock finally raises, believe it completely and let your good hands go.',
  sticker:
    'He hates folding, so his calls mean nothing and his raises mean everything. Value bet every decent hand relentlessly, cut your bluffs to almost none, and believe him the moment he raises.',
  firestarter:
    'The aggression is the leak, not a signal. Widen your calls, let him keep firing into your made hands, and never try to out-bluff a man who already bluffs far too much.',
  taxCollector:
    'He is solid, so respect his value. His edge is a narrow range, though, which misses a lot of flops, so float and take pots away on the low, connected boards his premiums skip.',
  surgeon:
    'There is no leak to attack here, so do not invent one. Play straightforward, believe his sizing, keep the pots small, and go win your money from the weaker players at the table.',
  sheriff:
    'He calls because he is sure you are bluffing, so stop bluffing him entirely. Value bet thinner and bigger than feels comfortable and let his weak pairs pay you off.',
  scaredStack:
    'He folds to protect his stack, so pressure prints against him. Bet and raise more than usual, lean on him near the money, and do not pay off the rare time he fights back.',
  waitingRoom:
    'Bet freely while he check-calls, because he is passive until he has it. But the sudden huge raise out of nowhere is never a bluff, so pay it off only with the goods and fold the rest.',
  tourist:
    'He calls with anything and his bet sizes are random, so ignore the noise. Keep it simple: value bet your good hands for more than feels polite, and never try to bluff someone who has to see it.',
  bully:
    'His pressure is mostly air, so stop folding to the size. Pick a hand you are happy to get all in, then let him barrel into it instead of trying to bluff back at him.',
  professor:
    'He plays face-up, so believe him. Fold when he bets big, and take the pot when he checks, because a check almost always means he has given up.',
  volcano:
    'When he is losing, his raises turn to spite and his calls turn to hope. Do not tangle with a fresh stack; just wait for a real hand and let the tilt pay you off.',
  promoChaser:
    'He is chasing the jackpot, not the pot, so he calls at any price. Value bet thin and never bluff him, but slow down when the board pairs or a flush lands, because that is the junk he stayed in for.',
  tableCaptain:
    'He raises to run the table, not because his cards say so, so the talk and the big bets are mostly show. Call down lighter, let him bluff off chips, and do not fold a real hand to a loud man.',
  shortStackGambler:
    'There is no middle gear: he limp-calls, then jams. When the stack goes in it is top pair or a draw he loves, so stack off only with hands that beat that and stop trying to bluff a short stack.',
};

/**
 * Project one archetype onto the shared popover content. `title`/`description`/`bullets` are the shipped
 * corpus (label / one-liner / the three tells); `example` is the authored adjustment. Total over every
 * ArchetypeId - `archetypeOf` throws on an unknown id (a corpus bug, never a user path), and ARCHETYPE_ADJUST
 * is a `Record<ArchetypeId, ...>` the compiler forces to be complete, so every type resolves to real content.
 */
export function archetypeExplainer(id: ArchetypeId): PopoverContent {
  const a = archetypeOf(id);
  return {
    // The FRIENDLY name (displayName), so the bubble title matches the name the player tapped everywhere
    // else - "The Vacuum" / "The Whale" / "The Firecracker", not the raw corpus label ("The Sticker" etc.).
    title: displayName(id),
    description: a.description,
    bulletsLabel: 'Tells',
    bullets: a.signals,
    exampleLabel: 'What it means for you:',
    example: ARCHETYPE_ADJUST[id],
  };
}

/** Every shipped archetype id (re-exported for callers that iterate the full set, e.g. the CX9 tests). */
export { ARCHETYPE_IDS };
